import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import RichTextEditor from '../../../components/RichTextEditor';

const CreateTaskModal = ({ isOpen, onClose, onSuccess, taskToCopy }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Получаем сегодняшнюю дату в формате YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Получаем дату 30 дней назад в формате YYYY-MM-DD
  const getMinDate = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().split('T')[0];
  };

  // Состояние формы
  const [formData, setFormData] = useState({
    site_id: '',
    d_start: getTodayDate(),
    d_end: getTodayDate(),
    pct: 0,
    status: 0,
    name: '',
    text: '',
  });

  // Загружаем список сайтов при открытии модалки
  useEffect(() => {
    if (isOpen) {
      fetchSites();
    }
  }, [isOpen]);

  // Заполняем форму данными копируемой задачи
  useEffect(() => {
    if (isOpen && taskToCopy) {
      setFormData({
        site_id: taskToCopy.site_id?.toString() || '',
        d_start: taskToCopy.d_start || getTodayDate(), // Сохраняем оригинальную дату начала
        d_end: taskToCopy.d_end || getTodayDate(), // Сохраняем оригинальную дату окончания
        pct: 0, // Сбрасываем прогресс для новой задачи
        status: 0, // Сбрасываем статус для новой задачи
        name: `(Копия) ${taskToCopy.name}`, // Добавляем "Копия:" в начало названия
        text: taskToCopy.text || '', // Копируем описание
      });
    } else if (isOpen && !taskToCopy) {
      // Сбрасываем форму при создании новой задачи
      setFormData({
        site_id: '',
        d_start: getTodayDate(),
        d_end: getTodayDate(),
        pct: 0,
        status: 0,
        name: '',
        text: '',
      });
    }
  }, [isOpen, taskToCopy]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки сайтов');
      }
    } catch (error) {
      console.error('Ошибка загрузки сайтов:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация
    if (!formData.site_id) {
      setError(currentLang === 'ru' ? 'Выберите сайт' : 'Please select a site');
      return;
    }
    
    if (!formData.name.trim()) {
      setError(currentLang === 'ru' ? 'Введите название задачи' : 'Please enter task name');
      return;
    }
    
    if (!formData.d_start) {
      setError(currentLang === 'ru' ? 'Выберите дату начала' : 'Please select start date');
      return;
    }
    
    if (!formData.d_end) {
      setError(currentLang === 'ru' ? 'Выберите дату окончания' : 'Please select end date');
      return;
    }

    // Проверяем, что дата окончания не раньше даты начала
    if (new Date(formData.d_end) < new Date(formData.d_start)) {
      setError(currentLang === 'ru' ? 'Дата окончания не может быть раньше даты начала' : 'End date cannot be earlier than start date');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          site_id: parseInt(formData.site_id),
          pct: parseInt(formData.pct),
          status: parseInt(formData.status),
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showNotification(currentLang === 'ru' ? 'Задача создана!' : 'Task created!', 'success');
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(data.message || 'Ошибка создания задачи');
      }
    } catch (error) {
      console.error('Ошибка создания задачи:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      site_id: '',
      d_start: getTodayDate(),
      d_end: getTodayDate(),
      pct: 0,
      status: 0,
      name: '',
      text: '',
    });
    setError('');
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Если изменилась дата начала и она позже даты окончания, обновляем дату окончания
      if (field === 'd_start' && value && new Date(value) > new Date(prev.d_end)) {
        newData.d_end = value;
      }

      return newData;
    });
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            {taskToCopy 
              ? (currentLang === 'ru' ? 'Копировать задачу' : 'Copy Task')
              : (currentLang === 'ru' ? 'Создать новую задачу' : 'Create New Task')
            }
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Ошибка */}
            {error && (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Сайт */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Сайт' : 'Site'} *
                </label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                >
                  <option value="">
                    {currentLang === 'ru' ? 'Выберите сайт...' : 'Select site...'}
                  </option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.dns} {site.status === 1 ? '(Активен)' : '(Неактивен)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Название задачи */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Название задачи' : 'Task name'} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={currentLang === 'ru' ? 'Введите название задачи' : 'Enter task name'}
                  required
                />
              </div>

              {/* Дата начала */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Дата начала' : 'Start date'} * <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    d_start: getTodayDate(),
                    d_end: getTodayDate()
                  }));
                }}
                className="px-3 py-1 text-sm text-brand-600 hover:text-brand-800 hover:bg-brand-50 rounded-md transition-colors"
              >
                {currentLang === 'ru' ? '📅 сегодня' : '📅  today'}
              </button>
                </label>
                <input
                  type="date"
                  value={formData.d_start}
                  onChange={(e) => handleInputChange('d_start', e.target.value)}
                  min={getMinDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {currentLang === 'ru' ? 'По умолчанию: сегодня (мин: 30 дней назад)' : 'Default: today (min: 30 days ago)'}
                </p>
              </div>

              {/* Дата окончания */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Дата окончания' : 'End date'} *
                </label>
                <input
                  type="date"
                  value={formData.d_end}
                  onChange={(e) => handleInputChange('d_end', e.target.value)}
                  min={formData.d_start || getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {currentLang === 'ru' ? 'По умолчанию: сегодня (мин: дата начала)' : 'Default: today (min: start date)'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Статус */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Статус' : 'Status'} *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  required
                >
                  <option value={0}>{currentLang === 'ru' ? 'Ожидает' : 'Pending'}</option>
                  <option value={1}>{currentLang === 'ru' ? 'В работе' : 'In Progress'}</option>
                  <option value={2}>{currentLang === 'ru' ? 'Завершена' : 'Completed'}</option>
                  <option value={99}>{currentLang === 'ru' ? 'Отменена' : 'Cancelled'}</option>
                </select>
              </div>

              {/* Прогресс */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {currentLang === 'ru' ? 'Прогресс' : 'Progress'} * ({formData.pct}%)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.pct}
                    onChange={(e) => handleInputChange('pct', parseInt(e.target.value) || 0)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Описание */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {currentLang === 'ru' ? 'Описание' : 'Description'}
              </label>
              <RichTextEditor
                value={formData.text}
                onChange={(content) => handleInputChange('text', content)}
                currentLang={currentLang}
                placeholder={currentLang === 'ru' ? 'Введите описание задачи...' : 'Enter task description...'}
              />
            </div>

            {/* Кнопки */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{currentLang === 'ru' ? 'Создание...' : 'Creating...'}</span>
                  </div>
                ) : (
                  taskToCopy 
                    ? (currentLang === 'ru' ? 'Создать копию' : 'Create Copy')
                    : (currentLang === 'ru' ? 'Создать задачу' : 'Create Task')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateTaskModal; 